import $ST from "plugins/tfs/platformConfigs/staticTextMaps/product";
import $P from "plugins/tfs/platformConfigs/integrationMaps/product";
import React, { useState } from "react";

// pageContainer
import ProductDetail from "_pageContainers/product/detail";

import Button from "theme/Button";
import Image from "theme/Image";
import Clear from "theme/Clear";
import Error from "theme/Error";
import ModalSpinner from "theme/ModalSpinner";
import Icon from "theme/Icon";
import AddToBasketModal from "theme/AddToBasketModalTFS";
import { thumbnailImageUrl, currencyText, objectSelector } from "theme/utils";
import Colors from "./Colors";
import Info from "./Info";
import DefaultVariant from "./DefaultVariant";
import $S from "theme/settings";
import GiftCardForm from "../giftCardForm";
import { notification, Icon as AntdIcon } from "antd";
import { Collapse, Divider } from "antd";
import {
  GIFT_CARD_SKU_LIST,
  MODAL_TEXT,
  MODAL_TEXT_GIFT_CARD,
  BASKET_COUNT,
} from "./constants";

const { Panel } = Collapse;

const showNotification = ({ title, text }) => {
  notification.open({
    message: title,
    description: text,
    icon: <AntdIcon type="warning" style={{ color: "#faad14" }} />,
    duration: 10,
  });
};

const basketHasAnyError = (hasProductInBasket, hasAnyGiftCardInBasket) => {
  let notification = null;

  if (hasAnyGiftCardInBasket) {
    notification = {
      title: MODAL_TEXT_GIFT_CARD.TITLE,
      text: MODAL_TEXT_GIFT_CARD.DESCRIPTION,
    };
  } else if (hasProductInBasket) {
    notification = {
      title: MODAL_TEXT.TITLE,
      text: MODAL_TEXT.DESCRIPTION,
    };
  }

  if (notification) {
    showNotification(notification);
    return true;
  }

  return false;
};

const productDetailGrid = (pageContext) => {
  const [currentImage, setCurrentImage] = useState(0);
  const [formData, setFormData] = useState({
    buyerFullName: "",
    personToBeGiftedEmail: "",
    personToBeGiftedName: "",
    message: "",
  });

  const [formDataErrors, setFormDataErrors] = useState({
    buyerFullName: "",
    personToBeGiftedEmail: "",
  });

  const {
    props: {
      product,
      variants,
      selected_variant,
      pending,
      error,
      basketError,
      success,
      basketItems,
    },
    successModal,
    handleChangeVariant,
    addBaskets,
    closeModal,
  } = pageContext;

  const isProductGiftCard = GIFT_CARD_SKU_LIST.includes(product.sku);
  const hasAnyGiftCardInBasket = basketItems.some((item) =>
    GIFT_CARD_SKU_LIST.includes(item.product.sku)
  );

  const handleAddToBasketForGiftCard = () => {
    if (basketHasAnyError(basketItems.length > 0, hasAnyGiftCardInBasket)) {
      return;
    }

    const isBuyerFullNameEmpty = !formData.buyerFullName.trim();
    const isPersonToBeGiftedEmailEmpty = !formData.personToBeGiftedEmail.trim();

    if (isBuyerFullNameEmpty || isPersonToBeGiftedEmailEmpty) {
      setFormDataErrors({
        ...(isBuyerFullNameEmpty && {
          buyerFullName: "Naam van de koper is verplicht",
        }),
        ...(isPersonToBeGiftedEmailEmpty && {
          personToBeGiftedEmail: "E-mail van de ontvanger is verplicht",
        }),
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.personToBeGiftedEmail)) {
      setFormDataErrors({
        buyerFullName: "",
        personToBeGiftedEmail: "Voer een geldig e-mailadres in",
      });
      return;
    }

    setFormDataErrors({});

    addBaskets(BASKET_COUNT, selected_variant.pk, {
      buyer_full_name: formData.buyerFullName,
      person_to_be_gifted_email: formData.personToBeGiftedEmail,
      person_to_be_gifted: formData.personToBeGiftedName,
      message: formData.message,
    });
  };

  const handleAddToBasket = () => {
    if (isProductGiftCard) {
      handleAddToBasketForGiftCard();
      return;
    }

    if (!selected_variant) return;

    if (hasAnyGiftCardInBasket) {
      showNotification({
        title: MODAL_TEXT.TITLE,
        text: MODAL_TEXT.DESCRIPTION,
      });
      return;
    }

    addBaskets(BASKET_COUNT, selected_variant.pk);
  };

  const renderThumbnails = (images) => {
    return images.map((img, i) => {
      return (
        <Button
          onClick={() => {
            setCurrentImage(i);
          }}
          key={i}
        >
          <Image
            src={thumbnailImageUrl(
              img.image,
              $S.productDetailThumbnailImageWidth,
              $S.productDetailThumbnailImageHeight,
              $S.productDetailThumbnailImageQuality
            )}
            width={$S.productDetailThumbnailImageWidth}
            height={$S.productDetailThumbnailImageHeight}
            classes={`thumbnail pointer ${currentImage === i ? "active" : ""} `}
          />
        </Button>
      );
    });
  };

  const renderPrice = (product) => {
    const basketOfferQuantity = objectSelector(
      product,
      "basket_offers.0.listing_kwargs.quantity"
    );
    const discountedTotalPrice = objectSelector(
      product,
      "basket_offers.0.listing_kwargs.discounted_total_price"
    );
    const isValidBasketOffer =
      basketOfferQuantity === 1 && !!discountedTotalPrice;

    if (isValidBasketOffer) {
      return (
        <div className="price-wrap">
          <span className="price retail-price">
            {currencyText(product.currency_type)} {product.retail_price}
          </span>
          <span className="price product-price">
            {currencyText(product.currency_type)} {discountedTotalPrice}
          </span>
        </div>
      );
    } else if (product.retail_price !== product.price) {
      return (
        <div className="price-wrap">
          <span className="price retail-price">
            {currencyText(product.currency_type)} {product.retail_price}
          </span>
          <span className="price product-price">
            {currencyText(product.currency_type)} {product.price}
          </span>
        </div>
      );
    } else {
      return (
        <div className="price-wrap">
          <span className="price product-price">
            {currencyText(product.currency_type)} {product.price}
          </span>
        </div>
      );
    }
  };

  const basketOffers = (product) => {
    if (product && product.basket_offers && product.basket_offers.length > 0) {
      return (
        <div className="basket_offers">
          <Divider />
          <ul>
            {product.basket_offers.map(({ label, pk }) => {
              return (
                <li key={pk}>
                  <span>{label}</span>
                </li>
              );
            })}
          </ul>
          <Divider />
        </div>
      );
    }
  };

  // eslint-disable-next-line no-unused-vars
  function renderProductDetail(product) {
    let {
      attributes: {
        integration_description2,
        integration_description3,
        integration_description4,
        integration_item_id,
      },
      name,
    } = product;

    integration_description2 =
      integration_description2 !== undefined &&
      integration_description2.split(";;;");
    return (
      <div>
        <Collapse defaultActiveKey={["1"]}>
          <Panel header={$ST.PRODUCT_DESCRIPTION} key="1">
            <div>
              {integration_description2 &&
                integration_description2.length &&
                integration_description2.map((item) => (
                  <p key={item} className="product__description">
                    {item}
                  </p>
                ))}
              <Divider />
              <p>{`${name} - ${integration_item_id}`}</p>
              <Divider />
              <p>{integration_description3}</p>
              <Divider />
              <p>{integration_description4}</p>
            </div>
          </Panel>
          <Panel header={$ST.DELIVERY_TO_RETURN} key="2">
            <p></p>
          </Panel>
        </Collapse>
      </div>
    );
  }

  const renderVariants = () => {
    return variants.map((variant, i) =>
      // eslint-disable-next-line array-callback-return
      $P.VARIANTS.map((item) => {
        if (variant.attribute_key === item.key) {
          switch (item.componentType) {
            case "color":
              return (
                <Colors
                  variant={variant}
                  key={i}
                  variantConfig={item}
                  variantSelection={(searchKey, variant) =>
                    handleChangeVariant(searchKey, variant)
                  }
                />
              );

            default:
              return (
                <DefaultVariant
                  data={variant}
                  variantConfig={item}
                  variantSelection={(searchKey, variant) =>
                    handleChangeVariant(searchKey, variant)
                  }
                  key={i}
                />
              );
          }
        }
      })
    );
  };

  return (
    <div className="product container headerMargin">
      {successModal && success && (
        <AddToBasketModal
          classes="text-center inner-centered"
          navigateButton1Text={"Continue Shopping"}
          navigateButton2Text={"Go to Cart"}
        >
          <div onClick={closeModal} className="close-button">
            <Icon icon="carpi" />
          </div>
          <p className="added-basket-text">{$ST.PRODUCT_ADDED_TO_BASKET}</p>
        </AddToBasketModal>
      )}
      {pending && <ModalSpinner />}
      {!pending && error.message && (
        <Error classes="text-center" message={$ST.ERROR_MESSAGE_FIRST} />
      )}
      {product !== undefined && product.name && !error.message && (
        <div className="row">
          <div className="col-12 col-md-7 images">
            <Image
              classes="active-image"
              src={thumbnailImageUrl(
                product.productimage_set[currentImage].image,
                $S.productDetailImageWidth,
                $S.productDetailImageHeight,
                $S.productDetailImageQuality
              )}
              height={$S.productDetailImageHeight}
            />
            <div className="d-flex thumbnails">
              {renderThumbnails(product.productimage_set)}
            </div>
          </div>
          <div className="col-12 col-md-5 content">
            <h1 className="title">{product.name}</h1>
            <div className="code">
              {$ST.PRODUCT_CODE}: {product.base_code}-
              {product.attributes[$P.PRODUCT_CODE]}
            </div>
            {renderPrice(product)}
            {basketOffers(product)}
            {renderVariants()}
            {isProductGiftCard && (
              <GiftCardForm
                formData={formData}
                setFormData={setFormData}
                formDataErrors={formDataErrors}
              />
            )}
            <Button
              classes={`button pointer float-left ${
                !selected_variant && "disabled"
              }`}
              onClick={handleAddToBasket}
            >
              {$ST.ADD_TO_BASKET}
            </Button>
            <Clear />
            <Error data={basketError} field="basketError" />
            <Info product={product} />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail(productDetailGrid);



// WEBPACK FOOTER //
// ./src/plugins/tfs/pages/product/detail/index.js